import { Pagination, Stack, Typography } from "@mui/material";
import { MprcContainer } from "components";
import { MprcFilterCountInfo } from "components/filter";
import { MprcFilterEmptyDataset } from "components/filter/MprcFilterEmptyDataset";
import { GlobalSearchOrderBy, KnowledgeSearchDtoPaginatedList, KnowledgeSearchFilters, KnowledgeSearchOption, KnowledgeSearchResponse } from "generated/CoreApi_v1";
import useDidUpdateEffect from "hooks/useNoInitialEffect";
import { INSIDE_LAYOUT_CONTENT_CONTAINER_ID } from "layouts/common/inside/MprcLayoutInside";
import { useCoreApi } from "providers/CoreApiProvider";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FullGlobalSearchButtonOptions, FullGlobalSearchOptionsLine, GlobalSearchRowsPerPage } from "views/general/GlobalSearch/components/GlobalSearchTabs";
import { SearchRenderPublication } from "views/general/GlobalSearch/components/SearchRenderPublication";
import { SearchRenderVideo } from "views/general/GlobalSearch/components/SearchRenderVideo";
import { useGlobalSearch } from "views/general/GlobalSearch/GlobalSearchProvider";

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////

const RenderPublications = (
    { knowledgeResult, showTitle, searchOption, onPageChanged }
        : {
            searchOption: KnowledgeSearchOption;
            knowledgeResult: KnowledgeSearchDtoPaginatedList;
            showTitle: boolean;
            onPageChanged: (page: number) => void;
        }
) => {
    const { t } = useTranslation("shared");

    if (knowledgeResult.totalRows == 0) return null;

    return (
        <div>
            {
                showTitle &&
                <MprcContainer>
                    <Typography variant="h4">
                        {t("GlobalSearch.FullSearch.PublicationTitle")}
                    </Typography>
                </MprcContainer>
            }
            <Stack direction="column" gap={3}>
                {
                    knowledgeResult.items.map((v, index) => {
                        return searchOption == KnowledgeSearchOption.Videos
                            ? <SearchRenderVideo
                                key={v.publicationId}
                                data={v}
                                index={index}
                            />
                            : <SearchRenderPublication
                                key={v.publicationId}
                                data={v}
                                index={index}
                            />;
                    })
                }
            </Stack>

            {
                knowledgeResult.totalPages > 1 &&
                <Pagination
                    shape="rounded"
                    color="primary"
                    page={knowledgeResult.pageNumber}
                    count={knowledgeResult.totalPages}
                    onChange={(e, pn) => onPageChanged(pn)}
                    sx={{ "& ul": { justifyContent: "center" } }}
                />
            }

        </div>
    );
};

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////

interface IKnowledgeProps {
    showTitle: boolean;
    renderFilter: React.ReactNode;
    filterEmptyTextKey: string;
    filterCountTextKey: string;
    searchOption: KnowledgeSearchOption;
    initialData?: KnowledgeSearchResponse | null;
    filters: KnowledgeSearchFilters;
}


export const KnowledgeSearchComponent = (props: IKnowledgeProps) => {
    const {
        searchOption,
        showTitle,
        renderFilter,
        filterEmptyTextKey,
        filterCountTextKey,
        initialData = null,
        filters
    } = props;

    const {
        globalSearchText,
        loading, setLoading,
        pageNumber, setPageNumber,
        filterOpened, setFilterOpened,
        selectedTab, scrollY, setScrollY,
        orderBy,
        setErrors: setLastError
    } = useGlobalSearch();

    const [knowledgeResult, setKnowledgeResult] = useState<KnowledgeSearchResponse | null>(initialData ?? null);
    const api = useCoreApi();
    const { t } = useTranslation();

    const filtering = useMemo(() => {
        return Object.entries(filters).filter(([key, value]) => key != "searchOption" && value != null).length > 0;
    }, [filters]);

    ///
    async function loadPublications(pageNumber: number) {
        setLoading(true);
        setLastError(null);

        try {
            const response = await api.v1.globalSearch.knowledgeSearch(
                {
                    searchText: globalSearchText,
                    filters: { ...filters, searchOption },
                    orderBy,
                    pageNumber,
                    pageSize: GlobalSearchRowsPerPage,
                    relevanceEnabled: pageNumber > 1 && (knowledgeResult?.relevanceEnabled ?? false)
                });
            setKnowledgeResult(response.data);
        }
        catch (e) {
            setLastError(e);
        }
        finally {
            setLoading(false);
        }
    }

    ///
    const renderSearch = () => {
        if (loading || knowledgeResult == null) return null;

        return knowledgeResult.knowledgeSearchResult.totalRows == 0
            ? <MprcFilterEmptyDataset message={t(filterEmptyTextKey)} />
            : (
                <>
                    <MprcContainer>
                        <MprcFilterCountInfo
                            text={t(filterCountTextKey, { count: knowledgeResult.knowledgeSearchResult.totalRows })}
                        />
                    </MprcContainer>

                    <RenderPublications
                        searchOption={searchOption}
                        showTitle={showTitle}
                        knowledgeResult={knowledgeResult.knowledgeSearchResult}
                        onPageChanged={(page) => setPageNumber(page)}
                    />
                </>
            );
    };

    /////////////////////////////////////////////////////////////////////////////////////////////////
    // Posiciona o Scroll ao retornar para a página de busca.
    useDidUpdateEffect(() => {
        const windowRef = document.getElementById(INSIDE_LAYOUT_CONTENT_CONTAINER_ID);
        if (windowRef && scrollY > 0) {
            windowRef.scrollTo(0, scrollY);
            setScrollY(0);
        }
    }, [knowledgeResult]);


    /// Ao alterar elementos de Filtro.
    useDidUpdateEffect(() => {
        if (pageNumber == 1 && !filtering && orderBy == GlobalSearchOrderBy.Relevance && initialData != null)
            setKnowledgeResult(initialData);
        else
            loadPublications(pageNumber);
    }, [selectedTab, filters, orderBy, pageNumber]);

    /// Quando os dados iniciais mudarem.
    useEffect(() => {
        if (pageNumber == 1 && !filtering && orderBy == GlobalSearchOrderBy.Relevance && initialData != null)
            setKnowledgeResult(initialData);
    }, [initialData]);

    const [el, setEl] = useState<HTMLElement | null>(null);
    useEffect(() => {
        setEl(document.getElementById("SearchTabFilterOptions") as HTMLElement);
    }, []);

    return (
        <>
            <FullGlobalSearchOptionsLine
                el={el}
                filterOpened={filterOpened}
                filter={renderFilter}
            />

            {(knowledgeResult?.knowledgeSearchResult.totalRows ?? 0) > 0 &&
                <FullGlobalSearchButtonOptions
                    filtering={filtering}
                    onFilterClick={() => setFilterOpened(!filterOpened)}
                />
            }

            {
                !loading && renderSearch()
            }
        </>
    );
};
